import { isViewOnlyProduct } from 'app/types/Product';
import { track } from "../core/App/track";
import { CtaButton } from "../core/components/CtaButton";
import { useMeDetailsQuery } from 'legoland-sdk/dist/experimental';
import { useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Text, tombac } from 'tombac';
import { SpinnerIcon } from 'tombac-icons';
import DashboardContactModal from "./DashboardContactForm/DashboardContactModal";
import DashboardEmptyState from "./DashboardEmptyState";
import "./DashboardPage.css";
import DashboardProductsContext from "./DashboardProductsContext";
import { MoreAboutTomTomAnalyticsCard, ProductCard } from "./ProductCard";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FetchContainer = styled.div.withConfig({
  displayName: "DashboardPage__FetchContainer"
})(["align-items:center;color:", ";display:flex;flex:1;justify-content:center;"], tombac.color('primary', 500));
const SupportLink = styled(CtaButton).withConfig({
  displayName: "DashboardPage__SupportLink"
})(["&:not(:first-child){margin-top:", ";}@media (min-width:600px){&:not(:first-child){margin-left:", ";margin-top:0;}}"], tombac.space(2), tombac.space(1));
const byName = (a, b) => a.displayName < b.displayName ? -1 : 1;
function DashboardPage() {
  const match = useRouteMatch();
  const meDetailsQuery = useMeDetailsQuery();
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactInitalTab, setContactInitialTab] = useState(undefined);
  const [wasUrlChanged, setWasUrlChanged] = useState(true);
  useEffect(() => {
    const {
      url
    } = match;
    if (/\/contact/.test(url) && wasUrlChanged) {
      setContactInitialTab(/\/contact\/sales/.test(url) ? 'sales' : 'support');
      setShowContactModal(true);
      setWasUrlChanged(false);
    }
  }, [match]);
  if (meDetailsQuery.isLoading) {
    return /*#__PURE__*/_jsx(FetchContainer, {
      children: /*#__PURE__*/_jsx(SpinnerIcon, {
        size: 2,
        spin: true
      })
    });
  }
  if (meDetailsQuery.isError) {
    return /*#__PURE__*/_jsx(FetchContainer, {
      children: /*#__PURE__*/_jsx(Text, {
        variant: "danger",
        children: "Could not load data."
      })
    });
  }
  if (meDetailsQuery.data.meDetails === null) {
    return null;
  }
  const {
    isFallback,
    products,
    email
  } = meDetailsQuery.data.meDetails;

  // Products that user has or had access to
  const usersProducts = products.filter(p => !isViewOnlyProduct(p)).sort(byName);

  // Products that user can't acces but can read about
  const viewProducts = products.filter(isViewOnlyProduct).sort(byName);
  return /*#__PURE__*/_jsx(DashboardProductsContext.Provider, {
    value: {
      products,
      reloadProducts: meDetailsQuery.refetch
    },
    "data-sentry-element": "unknown",
    "data-sentry-component": "DashboardPage",
    "data-sentry-source-file": "DashboardPage.tsx",
    children: /*#__PURE__*/_jsxs("div", {
      className: "DashboardPage",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "DashboardPage__content",
        children: [/*#__PURE__*/_jsxs("section", {
          className: "DashboardPage__products-section",
          children: [/*#__PURE__*/_jsx("h1", {
            className: "DashboardPage__section-title",
            children: "My products"
          }), usersProducts.length === 0 && /*#__PURE__*/_jsx(DashboardEmptyState, {}), usersProducts.length > 0 && /*#__PURE__*/_jsx("div", {
            className: "DashboardPage__product-list",
            children: usersProducts.map(p => /*#__PURE__*/_jsx(ProductCard, {
              product: p,
              userEmail: email
            }, p.id))
          })]
        }), viewProducts.length > 0 && /*#__PURE__*/_jsxs("section", {
          className: "DashboardPage__products-section",
          children: [/*#__PURE__*/_jsx("h2", {
            className: "DashboardPage__section-title",
            children: "There's more!"
          }), /*#__PURE__*/_jsx("div", {
            className: "DashboardPage__section-subtitle",
            children: "Take a look at our traffic products"
          }), /*#__PURE__*/_jsxs("div", {
            className: "DashboardPage__product-list",
            children: [viewProducts.map(p => /*#__PURE__*/_jsx(ProductCard, {
              product: p,
              userEmail: email
            }, p.id)), /*#__PURE__*/_jsx(MoreAboutTomTomAnalyticsCard, {})]
          })]
        })]
      }), /*#__PURE__*/_jsxs("section", {
        className: "DashboardPage__contact-section",
        children: [/*#__PURE__*/_jsx("div", {
          className: "DashboardPage__contact-section__title",
          children: "How can we help you?"
        }), /*#__PURE__*/_jsx("div", {
          className: "DashboardPage__contact-section__subtitle",
          children: "If you have questions, need help with upgrades, or any other product information, contact us."
        }), /*#__PURE__*/_jsxs("div", {
          className: "DashboardPage__contact-section__contactBtn",
          children: [!isFallback && /*#__PURE__*/_jsx(Button, {
            onClick: () => setShowContactModal(true),
            variant: "primary",
            children: "Contact us"
          }), /*#__PURE__*/_jsx(SupportLink, {
            forwardedAs: "a",
            href: "https://support.move.tomtom.com/",
            target: "_blank",
            onClick: () => {
              track('page_click', {
                category: 'dashboard',
                specificProps: {
                  component_name: 'contact-section',
                  component_sub_name: 'visit-movesupport-button',
                  click_href: 'https://support.move.tomtom.com/'
                }
              });
            },
            "data-sentry-element": "SupportLink",
            "data-sentry-source-file": "DashboardPage.tsx",
            "data-sentry-element": "SupportLink",
            children: "Visit Move support"
          })]
        }), /*#__PURE__*/_jsx(DashboardContactModal, {
          onRequestClose: () => setShowContactModal(false),
          initialTab: contactInitalTab,
          isOpen: showContactModal,
          "data-sentry-element": "DashboardContactModal",
          "data-sentry-source-file": "DashboardPage.tsx",
          "data-sentry-element": "DashboardContactModal"
        })]
      })]
    })
  });
}
export default DashboardPage;